import {
  Box,
  IconButton,
  Icon,
  FileUpload,
  Text,
  Flex,
} from '@chakra-ui/react';
import { LuUpload, LuX } from 'react-icons/lu';
import { useState, useEffect } from 'react';

interface UploadProps<T extends Record<string, unknown>> {
  name: string;
  values: T;
  setFieldValue: (name: string, value: unknown) => void;
}

export default function UploadComponent<T extends Record<string, unknown>>({
  name,
  values,
  setFieldValue,
}: UploadProps<T>) {
  const [files, setFiles] = useState<
    { file?: File; id: string; name: string }[]
  >([]);
  const [resetKey, setResetKey] = useState<number>(0);

  // Initialize files if value is a string (existing file path)
  const fieldValue = values[name];
  useEffect(() => {
    if (typeof fieldValue === 'string' && fieldValue) {
      const fileName = fieldValue.split('/').pop() || fieldValue;
      setFiles([{ id: `existing-${Date.now()}`, name: fileName }]);
    }
  }, [fieldValue, name]);

  const handleFileChange = (details: any) => {
    const file = details.acceptedFiles?.[0];
    if (!file) return;

    const uniqueFile = {
      file,
      id: `${file.name}-${Date.now()}`,
      name: file.name,
    };

    setFiles([uniqueFile]);
    setFieldValue(name, file);
  };

  const handleRemove = () => {
    setFiles([]);
    setFieldValue(name, null);
    setResetKey((k) => k + 1);
  };

  return (
    <FileUpload.Root
      key={resetKey}
      maxW="xl"
      alignItems="stretch"
      maxFiles={1}
      accept={{ 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] }}
      maxFileSize={5 * 1024 * 1024}
      onFileChange={handleFileChange}
    >
      <FileUpload.HiddenInput aria-label="Upload file" />

      {files.length === 0 && (
        <FileUpload.Dropzone>
          <Icon size="md" color="fg.muted">
            <LuUpload />
          </Icon>
          <FileUpload.DropzoneContent>
            <Box>Drag and drop file here</Box>
            <Box color="fg.muted">.png, .jpg up to 5MB</Box>
          </FileUpload.DropzoneContent>
        </FileUpload.Dropzone>
      )}

      {files.length > 0 && (
        <Flex
          alignItems={'center'}
          justifyContent={'space-between'}
          position="relative"
          height={'42px'}
        >
          {files.map((f) => (
            <Box key={f.id}>
              {<Text> {f.name} </Text>}

              {/* Show filename */}
            </Box>
          ))}

          <IconButton
            aria-label="Remove file"
            size="sm"
            position="absolute"
            top="2"
            right="2"
            onClick={handleRemove}
          >
            <LuX />
          </IconButton>
        </Flex>
      )}
    </FileUpload.Root>
  );
}
